import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Download } from '@mui/icons-material';

import {
  buildCsv,
  downloadBlobFile,
  downloadTextFile,
  getDefaultReportPeriod,
  getReportErrorMessage,
  ReportEmptyState,
  ReportErrorState,
  ReportPeriod,
  ReportPeriodFilter,
  ReportsNavigation,
  toApiPeriodParams,
} from '../../components/reports/ReportFilters';
import reportsService, { DeviceUptimeReport } from '../../services/reportsService';

const DevicesUptimePage: React.FC = () => {
  const [rows, setRows] = useState<DeviceUptimeReport[]>([]);
  const [period, setPeriod] = useState<ReportPeriod>(() => getDefaultReportPeriod());
  const [appliedPeriod, setAppliedPeriod] = useState<ReportPeriod>(() => getDefaultReportPeriod());
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const response = await reportsService.getDevicesUptime(toApiPeriodParams(appliedPeriod));
      setRows(response.data);
      setError(null);
    } catch (err) {
      setError(getReportErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [appliedPeriod]);

  useEffect(() => {
    load();
  }, [load]);

  const exportCsv = () => {
    const csv = buildCsv(rows, [
      { header: 'device_id', value: (row) => row.device_id },
      { header: 'device_name', value: (row) => row.device_name },
      { header: 'uptime_percentage', value: (row) => row.uptime_percentage },
      { header: 'total_hours', value: (row) => row.total_hours },
      { header: 'downtime_hours', value: (row) => row.downtime_hours },
      { header: 'last_online', value: (row) => row.last_online ?? '' },
      { header: 'status_changes', value: (row) => row.status_changes },
    ]);
    downloadTextFile(csv, `devices-uptime-${appliedPeriod.dateFrom}-${appliedPeriod.dateTo}.csv`);
  };

  const exportExcel = async () => {
    try {
      setExporting(true);
      const file = await reportsService.exportReport('devices-uptime', toApiPeriodParams(appliedPeriod), 'xlsx');
      downloadBlobFile(file, `devices-uptime-${appliedPeriod.dateFrom}-${appliedPeriod.dateTo}.xlsx`);
      setError(null);
    } catch (err) {
      setError(getReportErrorMessage(err, 'Не удалось экспортировать uptime в Excel'));
    } finally {
      setExporting(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Stack spacing={2}>
        <Box>
          <Typography variant="h4" fontWeight="bold">Время работы устройств</Typography>
          <Typography variant="body2" color="text.secondary">
            Uptime, простой и изменения статуса за выбранный период.
          </Typography>
        </Box>

        <ReportsNavigation />

        <ReportPeriodFilter
          period={period}
          onPeriodChange={setPeriod}
          onApply={() => setAppliedPeriod(period)}
          loading={loading}
          extraActions={(
            <Stack direction="row" spacing={1}>
              <Button variant="outlined" startIcon={<Download />} onClick={exportExcel} disabled={loading || exporting || rows.length === 0}>
                Excel
              </Button>
              <Button variant="outlined" startIcon={<Download />} onClick={exportCsv} disabled={loading || rows.length === 0}>
                CSV
              </Button>
            </Stack>
          )}
        />

        {error && <ReportErrorState title="Отчет не загрузился" message={error} onRetry={load} />}

        <Paper>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
          ) : rows.length === 0 ? (
            <ReportEmptyState title="Нет данных по uptime" message="За выбранный период устройства не дали событий для отчета." />
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Устройство</TableCell>
                  <TableCell>Uptime</TableCell>
                  <TableCell>Активно, ч</TableCell>
                  <TableCell>Простой, ч</TableCell>
                  <TableCell>Последний онлайн</TableCell>
                  <TableCell>Событий статуса</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.device_id}>
                    <TableCell>{row.device_name}</TableCell>
                    <TableCell>{row.uptime_percentage.toFixed(1)}%</TableCell>
                    <TableCell>{row.total_hours.toFixed(1)}</TableCell>
                    <TableCell>{row.downtime_hours.toFixed(1)}</TableCell>
                    <TableCell>{row.last_online ? new Date(row.last_online).toLocaleString() : '-'}</TableCell>
                    <TableCell>{row.status_changes}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Paper>
      </Stack>
    </Box>
  );
};

export default DevicesUptimePage;
